import { Show, createSignal } from "solid-js";
import { useDenominationsGridV2Context } from "../context/denominations_grid_v2_context";
import { toRupees } from "~/utils/number";
import { getContrastTextColor } from "~/utils/colors";

type GcAmountHeaderProps = {
  brandTitle: string;
  brandLogoUrl: string;
  brandColor: string;
  isFreeText: boolean;
};

function EditIcon(props: { color: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M5.293 15.6195L15.619 5.29348C16.009 4.90348 16.642 4.90348 17.032 5.29348L18.708 6.96948C19.098 7.35948 19.098 7.99248 18.708 8.38248L8.381 18.7075C8.194 18.8955 7.94 19.0005 7.675 19.0005H5V16.3255C5 16.0605 5.105 15.8065 5.293 15.6195Z"
        stroke={props.color}
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M13.75 7.16016L16.84 10.2502"
        stroke={props.color}
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export function GcAmountHeader(props: GcAmountHeaderProps) {
  const gridState = useDenominationsGridV2Context();
  const { internalState } = gridState;
  const { selectedAmount, simulationStore, setSelectedAmount } = internalState;

  const [isFocused, setIsFocused] = createSignal(false);
  let inputRef: HTMLInputElement | undefined;

  const contrastColor = () => getContrastTextColor(props.brandColor);

  return (
    <div
      class="relative flex aspect-[328/194] w-full flex-col items-center justify-center gap-3 overflow-clip rounded-lg border px-6 py-0"
      style={{
        position: "relative",
        "border-radius": "8px",
        background: `linear-gradient(${props.brandColor}, ${props.brandColor}) padding-box, linear-gradient(0deg, color-mix(in srgb, var(--background-normal-primary) 30%, transparent), color-mix(in srgb, var(--static-black) 30%, transparent)) border-box`,
        border: "1px solid transparent",
      }}
    >
      {/* Gradient overlay at top */}
      <div class="absolute left-1/2 top-[-27.93px] h-20 w-[260px] -translate-x-1/2">
        <div
          class="absolute inset-0"
          style={{
            background: `radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%)`,
            transform: "scale(2.5, 3.85)",
          }}
        />
      </div>

      {/* Brand Logo */}
      <div class="relative z-10 flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[rgba(255,255,255,0.1)] ">
        <img
          src={props.brandLogoUrl}
          alt={props.brandTitle}
          class="h-auto  w-full object-fill"
        />
      </div>

      {/* Amount Display */}
      <div
        onClick={() => props.isFreeText && inputRef?.focus()}
        class="relative z-10 flex h-11 w-[280px] shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-lg  px-6 py-3"
        classList={{
          "bg-[rgba(23,23,23,0.24)]": props.isFreeText,
        }}
      >
        <Show
          when={props.isFreeText}
          fallback={
            <div
              class="flex flex-col justify-center text-center text-[32px] font-medium leading-10 tracking-[-0.16px]"
              style={{ color: contrastColor() }}
            >
              <p class="leading-10">{toRupees(selectedAmount() || 0)}</p>
            </div>
          }
        >
          <input
            ref={inputRef}
            type="text"
            inputmode="numeric"
            class="w-full border-0 bg-transparent text-center text-[32px] font-medium leading-10 tracking-[-0.16px] outline-none focus:outline-none"
            style={{ color: contrastColor(), "caret-color": contrastColor() }}
            value={toRupees(selectedAmount() || 0)}
            onInput={(e) => {
              const value = e.currentTarget.value.replace(/[^0-9]/g, "");
              e.currentTarget.value = value;
              const numValue = parseInt(value);
              setSelectedAmount(isNaN(numValue) ? 0 : numValue);
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
          <Show when={!isFocused()}>
            <div
              class="absolute h-6 w-6 shrink-0 !transition-none"
              style={{
                right: `${Math.max(20, 110 - toRupees(selectedAmount() || 0).length * 10)}px`,
              }}
            >
              <EditIcon color={contrastColor()} />
            </div>
          </Show>
        </Show>
      </div>

      {/* Error Message Banner */}
      <Show when={simulationStore.error}>
        <div class="absolute bottom-0 left-0 flex w-full items-center justify-center bg-error-dark px-0 py-1">
          <div class="text-center text-label-regular text-static-white">
            {simulationStore.error}
          </div>
        </div>
      </Show>
    </div>
  );
}
